import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";

import { PERMISSIONS } from "../../constants";
import DialogConfirmation from "../generic/DialogConfirmation";
import AgreementForm from "./AgreementForm";
import {
  AGREEMENT_REQUEST_ERROR_LABELS,
  AGREEMENT_STATUS_COLOR,
  AGREEMENT_STATUS_LABELS,
  AGREEMENT_TYPE_LABELS,
  formatDate,
  formatMoney,
} from "./agreementLabels";
import { daysUntilEnd } from "./agreementLogic";
import {
  deleteAgreement,
  getAgreement,
  getAgreementLogs,
} from "../../services/backend/AgreementService";
import { removeAgreement, setCurrentAgreement, upsertAgreement } from "../../redux/slices/agreementSlice";

// Acción de la bitácora → texto. Los códigos los escribe handleAgreement.
const LOG_ACTION_LABELS = {
  CREATE: "Creó el convenio",
  UPDATE: "Editó el convenio",
  DELETE: "Eliminó el convenio",
};

const Field = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2">{children ?? "—"}</Typography>
  </Grid>
);

export default function AgreementView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const perms = user?.permissions || [];
  const roles = user?.roles || [];
  // ADMIN pasa por rol: agreement.* no existe en las cuentas creadas antes de
  // este módulo (los permisos son una foto por usuario).
  const isAdmin = roles.includes("ADMIN");
  const canUpdate = perms.includes(PERMISSIONS.AGREEMENT.UPDATE) || isAdmin;
  const canDelete = perms.includes(PERMISSIONS.AGREEMENT.DELETE) || isAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    const found = await getAgreement(code);
    setAgreement(found);
    dispatch(setCurrentAgreement(found));
    setLoading(false);
  }, [code, dispatch]);

  useEffect(() => { load(); }, [load]);

  const openLogs = async () => {
    setLogsOpen(true);
    setLogsLoading(true);
    setLogs(await getAgreementLogs(code, user));
    setLogsLoading(false);
  };

  const handleDelete = async () => {
    const result = await deleteAgreement(code, user);
    if (!result.ok) {
      setActionError(
        AGREEMENT_REQUEST_ERROR_LABELS[result.error] || "No se pudo eliminar el convenio."
      );
      return;
    }
    dispatch(removeAgreement(agreement.code));
    navigate("/agreements");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!agreement) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>Convenio no encontrado</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          El convenio {code} no existe o fue eliminado.
        </Typography>
        <Button onClick={() => navigate("/agreements")} startIcon={<ArrowBackIcon />}>
          Volver a convenios
        </Button>
      </Container>
    );
  }

  const remainingDays = daysUntilEnd(agreement.endDate);
  const percentage = agreement.committedPercentage || 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/agreements")}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>{agreement.code}</Typography>
        <Chip
          label={AGREEMENT_STATUS_LABELS[agreement.status] || agreement.status}
          color={AGREEMENT_STATUS_COLOR[agreement.status] || "default"}
        />
        {isAdmin && (
          <Tooltip title="Bitácora">
            <IconButton onClick={openLogs}><HistoryIcon /></IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <Button startIcon={<EditIcon />} onClick={() => setFormOpen(true)}>Editar</Button>
        )}
        {canDelete && (
          <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmOpen(true)}>
            Eliminar
          </Button>
        )}
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      {/* Vencido / por vencer: lo primero que necesita ver un gestor al abrir la ficha. */}
      {remainingDays !== null && remainingDays < 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          El convenio terminó el {formatDate(agreement.endDate)}.
        </Alert>
      )}
      {remainingDays !== null && remainingDays >= 0 && remainingDays <= 30 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Quedan {remainingDays} días de vigencia (termina el {formatDate(agreement.endDate)}).
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Objeto</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>{agreement.subject}</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Field label="Tipo">{AGREEMENT_TYPE_LABELS[agreement.type] || agreement.type}</Field>
          <Field label="Entidad contratante">
            {agreement.entityNit ? (
              // Enlace a la ficha de la entidad: desde el convenio se llega a su
              // documentación y a los demás convenios que sostiene.
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                sx={{ textAlign: "left" }}
                onClick={() => navigate(`/entities/${agreement.entityNit}`)}
              >
                {agreement.contractingEntity}
              </Link>
            ) : (
              agreement.contractingEntity
            )}
          </Field>
          <Field label="Área">{agreement.area}</Field>
          <Field label="Fecha de inicio">{formatDate(agreement.startDate)}</Field>
          <Field label="Fecha de terminación">{formatDate(agreement.endDate)}</Field>
          <Field label="Gestor responsable">{agreement.managerName}</Field>
          <Field label="Transferencia de conocimiento">
            {agreement.knowledgeTransfer ? "Sí" : "No"}
          </Field>
          <Field label="Registrado por">{agreement.createdByName}</Field>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Presupuesto</Typography>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Valor del convenio</Typography>
            <Typography variant="h6">{formatMoney(agreement.totalValue)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Comprometido</Typography>
            <Typography variant="h6">{formatMoney(agreement.committedValue)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Saldo</Typography>
            <Typography variant="h6">{formatMoney(agreement.remainingValue)}</Typography>
          </Grid>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={percentage >= 100 ? "error" : percentage >= 85 ? "warning" : "primary"}
          sx={{ height: 8, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {percentage}% comprometido
        </Typography>
        {/* El valor original sólo aporta cuando ya difiere del actual, que hoy
            sólo puede pasar editando el convenio a mano. */}
        {agreement.originalValue !== agreement.totalValue && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Valor original: {formatMoney(agreement.originalValue)} · vigencia original hasta{" "}
            {formatDate(agreement.originalEndDate)}
          </Typography>
        )}
      </Paper>

      <AgreementForm
        open={formOpen}
        agreement={agreement}
        onClose={() => setFormOpen(false)}
        onSaved={(saved) => {
          if (saved) dispatch(upsertAgreement({ ...saved, code: agreement.code }));
          load();
        }}
      />

      <DialogConfirmation
        confirmInfo={{
          open: confirmOpen,
          title: "Eliminar convenio",
          confirm: "Eliminar",
        }}
        content={`¿Eliminar el convenio ${agreement.code}? Dejará de aparecer en el listado, pero el documento se conserva.`}
        handleCloseDialog={(accepted) => {
          setConfirmOpen(false);
          if (accepted) handleDelete();
        }}
      />

      <Drawer anchor="right" open={logsOpen} onClose={() => setLogsOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }}>
          <Typography variant="h6" gutterBottom>Bitácora</Typography>
          {logsLoading && <CircularProgress size={22} />}
          {!logsLoading && logs.length === 0 && (
            <Typography variant="body2" color="text.secondary">Sin eventos registrados.</Typography>
          )}
          <List dense>
            {logs.map((log) => (
              <ListItem key={log.id} divider alignItems="flex-start">
                <ListItemText
                  primary={LOG_ACTION_LABELS[log.action] || log.action}
                  secondary={
                    <>
                      {log.userName}
                      {log.timestamp ? ` · ${new Date(log.timestamp).toLocaleString("es-CO")}` : ""}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Container>
  );
}
